import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import { useAuth } from '../auth/AuthContext'
import { api, getApiErrorMessage } from '../lib/api'
import { formatKoreanPhoneInput } from '../lib/crmInputFormat'
import { customerMaturityPolicyLines as maturityLines } from '../lib/customerMaturityPolicyLines'
import { PageFrame, PageHeader } from '../ui/PageLayout'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function addMonths(d, months) {
  return new Date(d.getFullYear(), d.getMonth() + months, 1)
}

function monthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function monthEnd(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

const PERIOD_ALL_KEY = 'all'

function buildMaturityPeriods(baseDate = new Date()) {
  const today = baseDate
  const monthPeriod = (offset, key) => {
    const anchor = addMonths(today, offset)
    return {
      key,
      label: `${anchor.getMonth() + 1}월`,
      caption: `${anchor.getMonth() + 1}월`,
      from: toYmd(monthStart(anchor)),
      to: toYmd(monthEnd(anchor)),
    }
  }
  return [
    { key: PERIOD_ALL_KEY, label: '전체', caption: '전체', from: null, to: null },
    { key: 'dueToday', label: '오늘', caption: '오늘', from: toYmd(today), to: toYmd(today) },
    monthPeriod(0, 'dueInCurrentMonth'),
    monthPeriod(1, 'dueInNextMonth'),
    monthPeriod(2, 'dueInMonthPlus2'),
  ]
}

function normalizePhoneDigits(value) {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length === 10 || digits.length === 11 ? digits : ''
}

function daysUntil(ymd) {
  if (!ymd) return null
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const target = new Date(`${ymd}T00:00:00`)
  if (Number.isNaN(target.getTime())) return null
  return Math.round((target.getTime() - start.getTime()) / 86400000)
}

function formatPhone(v) {
  const digits = String(v || '').replace(/\D/g, '')
  return digits ? formatKoreanPhoneInput(digits) : '-'
}

function formatRemainingDays(days) {
  if (days == null) return '-'
  if (days < 0) return `${Math.abs(days)}일 지남`
  if (days === 0) return '오늘'
  return `${days}일 남음`
}

function contractLabel(c) {
  if (!c) return '계약 연결 안 함'
  const parts = [c.insurerName, c.productName, c.policyNumberMasked, c.coveragePeriod].filter(Boolean)
  return parts.length ? parts.join(' · ') : `계약 #${c.id}`
}

const editableFields = [
  'policyLine',
  'nextMaturityDate',
  'insurerName',
  'productLabel',
  'enrollmentChannel',
  'vehicleNumber',
  'driverRestriction',
]

function toEditableDraft(row = {}) {
  return {
    customerRefId: row.customerRefId ? String(row.customerRefId) : '',
    customerName: row.customerName || '',
    customerPhone: row.customerPhone || '',
    policyLine: row.policyLine || 'AUTO',
    nextMaturityDate: row.nextMaturityDate || '',
    insurerName: row.insurerName || '',
    productLabel: row.productLabel || row.memo || '',
    enrollmentChannel: row.enrollmentChannel || '',
    vehicleNumber: row.vehicleNumber || '',
    driverRestriction: row.driverRestriction || '',
    linkedContractId: row.linkedContractId ? String(row.linkedContractId) : '',
  }
}

function emptyNewDraft() {
  return {
    tempId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ...toEditableDraft(),
  }
}

function draftPayload(draft) {
  return {
    policyLine: draft.policyLine || 'AUTO',
    nextMaturityDate: draft.nextMaturityDate,
    insurerName: String(draft.insurerName || '').trim() || null,
    productLabel: String(draft.productLabel || '').trim() || null,
    enrollmentChannel: String(draft.enrollmentChannel || '').trim() || null,
    vehicleNumber: draft.policyLine === 'AUTO' ? String(draft.vehicleNumber || '').trim() || null : null,
    driverRestriction: draft.policyLine === 'AUTO' ? String(draft.driverRestriction || '').trim() || null : null,
    linkedContractId: draft.linkedContractId ? Number(draft.linkedContractId) : 0,
  }
}

function isChangedDraft(row, draft) {
  const original = toEditableDraft(row)
  return editableFields.some((field) => String(original[field] || '') !== String(draft[field] || ''))
}

function isValidPolicyLine(value, options = maturityLines) {
  return options.some((line) => line.key === value)
}

function safeFilenamePart(value, fallback = '사용자') {
  const text = String(value || '').trim() || fallback
  return text.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_')
}

function filenameFromDisposition(headers, fallback) {
  const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'] || ''
  if (typeof cd === 'string') {
    const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
    if (star?.[1]) {
      try { return decodeURIComponent(star[1]) } catch { }
    }
    const plain = cd.match(/filename="([^"]+)"/i)
    if (plain?.[1]) return plain[1]
  }
  return fallback
}

function isAutoCreatedFromMaturityUpload(row) {
  return String(row?.customerSource || '').trim() === '보험만기대장 업로드'
}

function dailyBriefingAlimtalkBadge(days) {
  if (![1, 7, 30].includes(days)) return ''
  return `D-${days} 알림톡`
}

const policyLineDashboardClasses = {
  AUTO: 'bg-sky-50/40',
  GENERAL: 'bg-emerald-50/40',
  FIRE: 'bg-rose-50/40',
  DRIVER: 'bg-amber-50/40',
  HEALTH: 'bg-teal-50/40',
  INDEMNITY: 'bg-cyan-50/40',
  WHOLE_LIFE: 'bg-indigo-50/40',
  ETC: 'bg-slate-50',
}

const policyLineTableClasses = {
  AUTO: 'bg-sky-50/20 hover:bg-sky-50/50',
  GENERAL: 'bg-emerald-50/20 hover:bg-emerald-50/50',
  FIRE: 'bg-rose-50/20 hover:bg-rose-50/50',
  DRIVER: 'bg-amber-50/20 hover:bg-amber-50/50',
  HEALTH: 'bg-teal-50/20 hover:bg-teal-50/50',
  INDEMNITY: 'bg-cyan-50/20 hover:bg-cyan-50/50',
  WHOLE_LIFE: 'bg-indigo-50/20 hover:bg-indigo-50/50',
  ETC: 'bg-slate-50/60 hover:bg-slate-100/70',
}

function policyLineDashboardClass(key) {
  return policyLineDashboardClasses[key] || 'bg-slate-50'
}

function policyLineTableClass(key) {
  return policyLineTableClasses[key] || 'hover:bg-slate-50'
}
const sheetInputClass = 'w-full min-w-0 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs'
const sheetSelectClass = 'w-full min-w-0 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs'
const DEFAULT_PAGE_SIZE = 10


function dashboardLineValue(dashboard, line, periodKey) {
  const nested = dashboard?.byPolicyLine?.[line]?.[periodKey]
  if (typeof nested === 'number') return nested
  return 0
}

function dashboardTotalValue(dashboard, periodKey, lines = maturityLines) {
  const direct = dashboard?.[periodKey]
  if (typeof direct === 'number') return direct
  return lines.reduce((sum, line) => sum + dashboardLineValue(dashboard, line.key, periodKey), 0)
}

function buildListParams({ dateRange, policyLine, page, size, query, sortKey, sortDirection }) {
  const params = {
    policyLine,
    page,
    size,
    sort: sortKey || 'nextMaturityDate',
    direction: sortDirection || 'ASC',
  }
  const q = String(query || '').trim()
  if (q) {
    params.q = q
  }
  if (dateRange.from && dateRange.to) {
    params.from = dateRange.from
    params.to = dateRange.to
  }
  return params
}

function buildExcelParams({ dateRange, policyLine, query, sortKey, sortDirection }) {
  const params = {
    policyLine,
    sort: sortKey || 'nextMaturityDate',
    direction: sortDirection || 'ASC',
  }
  const q = String(query || '').trim()
  if (q) params.q = q
  if (dateRange.from && dateRange.to) {
    params.from = dateRange.from
    params.to = dateRange.to
  }
  return params
}

export default function CustomerMaturityLedgerPage() {
  const csvInputRef = useRef(null)
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [pageInfo, setPageInfo] = useState({ page: 0, size: DEFAULT_PAGE_SIZE, totalElements: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState(null)
  const [dashboardOpen, setDashboardOpen] = useState(false)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingRowKeys, setSavingRowKeys] = useState({})
  const [csvBusy, setCsvBusy] = useState(false)
  const [err, setErr] = useState('')
  const [rowErrors, setRowErrors] = useState({})
  const [draftRows, setDraftRows] = useState({})
  const [newRows, setNewRows] = useState([])
  const [periodKey, setPeriodKey] = useState(PERIOD_ALL_KEY)
  const [policyLine, setPolicyLine] = useState('ALL')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortKey, setSortKey] = useState('nextMaturityDate')
  const [sortDirection, setSortDirection] = useState('ASC')

  const maturityPeriods = useMemo(() => buildMaturityPeriods(), [])
  const visiblePeriod = maturityPeriods.find((p) => p.key === periodKey) || maturityPeriods[0]
  const policyLineOptions = useMemo(() => {
    const serverLines = Array.isArray(dashboard?.policyLines) ? dashboard.policyLines : []
    const normalized = serverLines
      .map((line) => ({
        key: line?.code || line?.key,
        label: line?.label || line?.shortLabel || line?.code || line?.key,
        shortLabel: line?.shortLabel || line?.label || line?.code || line?.key,
        aliases: [],
      }))
      .filter((line) => line.key)
    return normalized.length > 0 ? normalized : maturityLines
  }, [dashboard?.policyLines])

  const dateRange = useMemo(() => ({
    from: visiblePeriod.from,
    to: visiblePeriod.to,
  }), [visiblePeriod.from, visiblePeriod.to])
  const periodDescription = visiblePeriod.key === PERIOD_ALL_KEY ? '전체 기간' : `${dateRange.from} ~ ${dateRange.to}`
  const csvOwnerName = safeFilenamePart(user?.name || user?.displayName || user?.userName || user?.email)

  const loadRows = useCallback(async (nextPage = pageInfo.page) => {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get('/customer-management/maturity-policies', {
        params: buildListParams({
          dateRange,
          policyLine,
          page: nextPage,
          size: pageInfo.size,
          query: searchQuery,
          sortKey,
          sortDirection,
        }),
      })
      const data = unwrap(res) || {}
      setRows(Array.isArray(data.items) ? data.items : [])
      setRowErrors({})
      setPageInfo({
        page: typeof data.page === 'number' ? data.page : nextPage,
        size: typeof data.size === 'number' ? data.size : pageInfo.size,
        totalElements: typeof data.totalElements === 'number' ? data.totalElements : 0,
        totalPages: typeof data.totalPages === 'number' ? data.totalPages : 0,
      })
    } catch (e) {
      const msg = getApiErrorMessage(e, '보험만기대장을 불러오지 못했습니다.')
      setErr(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [dateRange, pageInfo.page, pageInfo.size, policyLine, searchQuery, sortDirection, sortKey])

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true)
    try {
      const res = await api.get('/customer-management/maturity-policies/dashboard', { __silent: true })
      const data = unwrap(res) || {}
      setDashboard(data && typeof data === 'object' ? data : null)
    } catch {
      setDashboard(null)
    } finally {
      setDashboardLoading(false)
    }
  }, [])

  useEffect(() => {
    setPageInfo((prev) => ({ ...prev, page: 0 }))
    loadRows(0)
    loadDashboard()
  }, [dateRange.from, dateRange.to, policyLine, searchQuery, sortDirection, sortKey, loadDashboard]) // eslint-disable-line react-hooks/exhaustive-deps

  const rowDraft = (row) => draftRows[row.id] || toEditableDraft(row)

  const setDraftField = (row, field, value) => {
    if (savingRowKeys['row-' + row.id]) return
    setDraftRows((prev) => {
      const base = prev[row.id] || toEditableDraft(row)
      const next = { ...base, [field]: value }
      if (field === 'policyLine' && value !== 'AUTO') {
        next.vehicleNumber = ''
        next.driverRestriction = ''
      }
      if (!isChangedDraft(row, next)) {
        const copy = { ...prev }
        delete copy[row.id]
        return copy
      }
      return { ...prev, [row.id]: next }
    })
  }

  const setNewField = (tempId, field, value) => {
    if (savingRowKeys['new-' + tempId]) return
    setNewRows((prev) => prev.map((row) => {
      if (row.tempId !== tempId) return row
      const next = { ...row, [field]: value }
      if (field === 'policyLine' && value !== 'AUTO') {
        next.vehicleNumber = ''
        next.driverRestriction = ''
      }
      if ((field === 'customerName' || field === 'customerPhone') && row.customerRefId) {
        next.customerRefId = ''
      }
      return next
    }))
  }

  const applyManagedCustomerToNewRow = (tempId, customer) => {
    if (!customer || savingRowKeys['new-' + tempId]) return
    setNewRows((prev) => prev.map((row) => {
      if (row.tempId !== tempId) return row
      const phoneDigits = String(customer.customerPhone || '').replace(/\D/g, '')
      return {
        ...row,
        customerRefId: String(customer.id || ''),
        customerName: customer.customerName || row.customerName,
        customerPhone: phoneDigits ? formatKoreanPhoneInput(phoneDigits) : row.customerPhone,
      }
    }))
  }

  const clearManagedCustomerFromNewRow = (tempId) => {
    if (savingRowKeys['new-' + tempId]) return
    setNewRows((prev) => prev.map((row) => (
      row.tempId === tempId
        ? { ...row, customerRefId: '', customerPhone: '' }
        : row
    )))
  }

  const addNewRow = () => {
    setNewRows((prev) => [...prev, emptyNewDraft()])
  }

  const removeNewRow = (tempId) => {
    setNewRows((prev) => prev.filter((row) => row.tempId !== tempId))
    setRowErrors((prev) => {
      const copy = { ...prev }
      delete copy[`new-${tempId}`]
      return copy
    })
  }

  const discardDrafts = () => {
    setDraftRows({})
    setNewRows([])
    setRowErrors({})
  }

  const setRowSaving = (key, isSaving) => {
    setSavingRowKeys((prev) => {
      if (isSaving) return { ...prev, [key]: true }
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const clearRowError = (key) => {
    setRowErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const discardRowDraft = (rowId) => {
    setDraftRows((prev) => {
      if (!prev[rowId]) return prev
      const next = { ...prev }
      delete next[rowId]
      return next
    })
    clearRowError('row-' + rowId)
  }
  const validateDraft = (draft, needsCustomer) => {
    if (needsCustomer) {
      if (!draft.customerRefId) {
        const name = String(draft.customerName || '').trim()
        const phone = normalizePhoneDigits(draft.customerPhone)
        if (!name) return '이름을 입력해 주세요.'
        if (!phone) return '연락처는 숫자 10~11자리로 입력해 주세요.'
      }
    }
    if (!isValidPolicyLine(draft.policyLine, policyLineOptions)) return '구분을 선택해 주세요.'
    if (!draft.nextMaturityDate) return '다음 만기일을 입력해 주세요.'
    return ''
  }

  const hasNewRowContent = (row) => (
    row.customerRefId
    || row.customerName
    || row.customerPhone
    || row.nextMaturityDate
    || row.insurerName
    || row.productLabel
    || row.enrollmentChannel
    || row.vehicleNumber
    || row.driverRestriction
  )

  const saveSheet = async () => {
    const changedRows = rows
      .filter((row) => draftRows[row.id] && isChangedDraft(row, draftRows[row.id]))
      .map((row) => ({ row, draft: draftRows[row.id] }))
    const insertRows = newRows.filter(hasNewRowContent)

    if (changedRows.length === 0 && insertRows.length === 0) {
      try { window?.toast?.show?.('저장할 변경사항이 없습니다.', 'info') } catch { }
      return
    }

    const nextErrors = {}
    changedRows.forEach(({ row, draft }) => {
      const message = validateDraft(draft, false)
      if (message) nextErrors[`row-${row.id}`] = message
    })
    insertRows.forEach((draft) => {
      const message = validateDraft(draft, true)
      if (message) nextErrors[`new-${draft.tempId}`] = message
    })
    if (Object.keys(nextErrors).length > 0) {
      setRowErrors(nextErrors)
      try { window?.toast?.show?.('저장 전 확인이 필요한 행이 있습니다.', 'error') } catch { }
      return
    }

    setSaving(true)
    setErr('')
    setRowErrors({})
    try {
      const batchRows = [
        ...changedRows.map(({ row, draft }, idx) => ({
          clientId: String(idx + 1),
          policyId: Number(row.id),
          customerRefId: Number(row.customerRefId),
          customerName: row.customerName || null,
          customerPhone: row.customerPhone || null,
          ...draftPayload(draft),
        })),
        ...insertRows.map((draft, idx) => ({
          clientId: String(changedRows.length + idx + 1),
          policyId: null,
          customerRefId: draft.customerRefId ? Number(draft.customerRefId) : null,
          customerName: String(draft.customerName || '').trim() || null,
          customerPhone: normalizePhoneDigits(draft.customerPhone) || null,
          ...draftPayload(draft),
        })),
      ]
      const res = await api.post('/customer-management/maturity-policies/batch', { rows: batchRows }, { __silent: true })
      const data = unwrap(res) || {}
      const created = Number(data.created || 0)
      const updated = Number(data.updated || 0)
      try { window?.toast?.show?.(`만기대장 저장 완료: 수정 ${updated}건, 추가 ${created}건`, 'success') } catch { }
      setDraftRows({})
      setNewRows([])
      await loadRows(insertRows.length > 0 ? 0 : pageInfo.page)
      await loadDashboard()
    } catch (e) {
      const msg = getApiErrorMessage(e, '만기대장 변경사항 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setSaving(false)
    }
  }
  const saveExistingRow = async (row) => {
    const draft = draftRows[row.id]
    if (!draft || !isChangedDraft(row, draft) || saving || Object.keys(savingRowKeys).length > 0) return

    const rowKey = 'row-' + row.id
    const validationError = validateDraft(draft, false)
    if (validationError) {
      setRowErrors((prev) => ({ ...prev, [rowKey]: validationError }))
      try { window?.toast?.show?.('저장 전 확인이 필요한 항목이 있습니다.', 'error') } catch { }
      return
    }

    setRowSaving(rowKey, true)
    setErr('')
    clearRowError(rowKey)
    try {
      await api.post('/customer-management/maturity-policies/batch', {
        rows: [{
          clientId: rowKey,
          policyId: Number(row.id),
          customerRefId: Number(row.customerRefId),
          customerName: row.customerName || null,
          customerPhone: row.customerPhone || null,
          ...draftPayload(draft),
        }],
      }, { __silent: true })
      discardRowDraft(row.id)
      await Promise.all([loadRows(pageInfo.page), loadDashboard()])
      try { window?.toast?.show?.('만기 계약을 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '만기 계약 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setRowSaving(rowKey, false)
    }
  }

  const saveNewRow = async (draft) => {
    if (!hasNewRowContent(draft) || saving || Object.keys(savingRowKeys).length > 0) return

    const rowKey = 'new-' + draft.tempId
    const validationError = validateDraft(draft, true)
    if (validationError) {
      setRowErrors((prev) => ({ ...prev, [rowKey]: validationError }))
      try { window?.toast?.show?.('저장 전 확인이 필요한 항목이 있습니다.', 'error') } catch { }
      return
    }

    setRowSaving(rowKey, true)
    setErr('')
    clearRowError(rowKey)
    try {
      await api.post('/customer-management/maturity-policies/batch', {
        rows: [{
          clientId: rowKey,
          policyId: null,
          customerRefId: draft.customerRefId ? Number(draft.customerRefId) : null,
          customerName: String(draft.customerName || '').trim() || null,
          customerPhone: normalizePhoneDigits(draft.customerPhone) || null,
          ...draftPayload(draft),
        }],
      }, { __silent: true })
      removeNewRow(draft.tempId)
      await Promise.all([loadRows(pageInfo.page), loadDashboard()])
      try { window?.toast?.show?.('신규 만기 계약을 저장했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '신규 만기 계약 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setRowSaving(rowKey, false)
    }
  }
  const downloadCsv = async () => {
    setCsvBusy(true)
    setErr('')
    try {
      const res = await api.get('/customer-management/maturity-policies/excel', {
        params: buildExcelParams({ dateRange, policyLine, query: searchQuery, sortKey, sortDirection }),
        responseType: 'blob',
        __silent: true,
      })
      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filenameFromDisposition(res.headers, `${csvOwnerName}_보험만기대장_${toYmd(new Date()).replace(/-/g, '')}.xlsx`)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = getApiErrorMessage(e, '엑셀 다운로드에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setCsvBusy(false)
    }
  }

  const uploadCsv = async (file) => {
    if (!file) return
    setCsvBusy(true)
    setErr('')
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/customer-management/maturity-policies/excel', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        __silent: true,
      })
      const data = unwrap(res) || {}
      const created = Number(data.created || 0)
      const updated = Number(data.updated || 0)
      const customersEnsured = Number(data.customersEnsured || 0)
      const ensuredMsg = customersEnsured > 0 ? `, 고객 확인 ${customersEnsured}건` : ''
      try { window?.toast?.show?.(`파일 반영 완료: 업데이트 ${updated}건, 신규 ${created}건${ensuredMsg}`, 'success') } catch { }
      setNewRows([])
      setDraftRows({})
      setRowErrors({})
      await loadRows(pageInfo.page)
      await loadDashboard()
    } catch (e) {
      const msg = getApiErrorMessage(e, '파일 업로드에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setCsvBusy(false)
    }
  }
  const closeRow = async (row) => {
    if (!row?.id || !row?.customerRefId || saving || Object.keys(savingRowKeys).length > 0) return
    const rowKey = 'row-' + row.id
    const hasRowChanges = Boolean(draftRows[row.id] && isChangedDraft(row, draftRows[row.id]))
    if (hasRowChanges && !window.confirm('저장하지 않은 이 행의 변경사항이 있습니다. 변경사항을 버리고 종료할까요?')) return
    if (!window.confirm('이 만기·갱신 행을 종료하고 자동 등록된 안내 일정을 취소할까요?')) return
    const targetPage = rows.length <= 1 && pageInfo.page > 0 ? pageInfo.page - 1 : pageInfo.page
    setRowSaving(rowKey, true)
    setErr('')
    try {
      await api.delete('/customer-management/customers/' + row.customerRefId + '/maturity-policies/' + row.id, { __silent: true })
      discardRowDraft(row.id)
      await loadRows(targetPage)
      await loadDashboard()
      try { window?.toast?.show?.('만기·갱신 행을 종료했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '종료 처리에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setRowSaving(rowKey, false)
    }
  }
  const canPrev = pageInfo.page > 0
  const canNext = pageInfo.totalPages > 0 && pageInfo.page + 1 < pageInfo.totalPages
  const changedCount = rows.filter((row) => draftRows[row.id] && isChangedDraft(row, draftRows[row.id])).length
  const newCount = newRows.filter(hasNewRowContent).length
  const hasSheetChanges = changedCount > 0 || newCount > 0
  const isSavingAnyRow = Object.keys(savingRowKeys).length > 0
  const sortLabel = sortKey === 'remainingDays' ? '남은 일' : '만기일'
  const sortDirectionLabel = sortDirection === 'DESC' ? '내림차순' : '오름차순'
  const sortHeaderMark = (key) => (sortKey === key ? (sortDirection === 'ASC' ? '↑' : '↓') : '↕')

  const confirmDiscardChanges = () => (
    !hasSheetChanges || window.confirm('저장하지 않은 변경사항이 있습니다. 변경사항을 버리고 이동할까요?')
  )

  const runAfterDiscardCheck = (action) => {
    if (!confirmDiscardChanges()) return false
    if (hasSheetChanges) discardDrafts()
    action()
    return true
  }

  useEffect(() => {
    if (!hasSheetChanges) return undefined
    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasSheetChanges])

  const applySearch = (event) => {
    event.preventDefault()
    const next = searchInput.trim()
    runAfterDiscardCheck(() => {
      setPageInfo((prev) => ({ ...prev, page: 0 }))
      if (next === searchQuery) {
        loadRows(0)
        return
      }
      setSearchQuery(next)
    })
  }

  const clearSearch = () => {
    runAfterDiscardCheck(() => {
      setSearchInput('')
      setPageInfo((prev) => ({ ...prev, page: 0 }))
      if (searchQuery) {
        setSearchQuery('')
        return
      }
      loadRows(0)
    })
  }

  const toggleSort = (nextKey) => {
    runAfterDiscardCheck(() => {
      setPageInfo((prev) => ({ ...prev, page: 0 }))
      if (sortKey === nextKey) {
        setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))
        return
      }
      setSortKey(nextKey)
      setSortDirection('ASC')
    })
  }

  const changePeriod = (nextPeriodKey) => {
    runAfterDiscardCheck(() => setPeriodKey(nextPeriodKey))
  }

  const changePolicyLine = (nextPolicyLine) => {
    runAfterDiscardCheck(() => setPolicyLine(nextPolicyLine))
  }

  const changeDashboardSelection = (nextPolicyLine, nextPeriodKey) => {
    runAfterDiscardCheck(() => {
      setPolicyLine(nextPolicyLine)
      setPeriodKey(nextPeriodKey)
    })
  }

  const refreshAll = () => {
    runAfterDiscardCheck(() => {
      loadRows(pageInfo.page)
      loadDashboard()
    })
  }

  const handleUploadFile = (file) => {
    if (!file) return
    runAfterDiscardCheck(() => uploadCsv(file))
  }

  const goToPage = (nextPage) => {
    runAfterDiscardCheck(() => loadRows(nextPage))
  }
  return (
    <PageFrame>
      <PageHeader title="보험만기대장" />

      {err ? (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-white">
          <ErrorState description={err} onAction={refreshAll} className="py-6" />
        </div>
      ) : null}

      <section className="mb-4">
        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs font-bold text-slate-900">만기관리 대시보드</div>
              {dashboardLoading ? <span className="text-xs text-slate-400">요약 갱신 중...</span> : null}
            </div>
            <div className="mt-1 text-xs font-medium leading-5 text-slate-500 sm:text-sm">
              알림톡 기준: D-30, D-7, D-1 만기 계약은 당일 08:00 업무 브리핑 알림톡 발송 대상에 포함됩니다.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`rounded border px-2.5 py-1 text-xs font-semibold shadow-sm transition ${dashboardOpen
                ? 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100'
                : 'border-brand-sky/40 bg-brand-sky/10 text-brand-navy hover:bg-brand-sky/15'
                }`}
              aria-expanded={dashboardOpen}
              onClick={() => setDashboardOpen((open) => !open)}
            >
              {dashboardOpen ? '접기' : '펼치기'}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={refreshAll}
              disabled={loading}
            >
              새로고침
            </button>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-sgez-surface">
          <div className="min-w-[560px]">
            <div className="grid grid-cols-[104px_repeat(5,minmax(0,1fr))] bg-slate-50 text-[11px] font-bold text-slate-500">
              <div className="px-2 py-1.5">구분</div>
              {maturityPeriods.map((period) => (
                <div key={period.key} className="px-2 py-1.5 text-right">{period.caption}</div>
              ))}
            </div>
            <div className="grid grid-cols-[104px_repeat(5,minmax(0,1fr))] border-t border-slate-200 bg-slate-50">
              <div className="flex items-center px-2 py-1.5 text-xs font-black text-slate-700">전체</div>
              {maturityPeriods.map((period) => (
                <button
                  key={`total-${period.key}`}
                  type="button"
                  className={`border-l border-slate-200 px-2 py-1.5 text-right transition ${policyLine === 'ALL' && periodKey === period.key ? 'bg-sky-50 text-brand-navy ring-1 ring-inset ring-brand-sky/30' : 'hover:bg-white'
                    }`}
                  onClick={() => changeDashboardSelection('ALL', period.key)}
                >
                  <div className="text-base font-black tabular-nums">{Number(dashboardTotalValue(dashboard, period.key, policyLineOptions) || 0).toLocaleString()}</div>
                </button>
              ))}
            </div>
            {dashboardOpen ? policyLineOptions.map((line) => (
              <div
                key={line.key}
                className={`grid grid-cols-[104px_repeat(5,minmax(0,1fr))] border-t border-slate-100 ${policyLineDashboardClass(line.key)}`}
              >
                <div className="flex items-center px-2 py-2 text-xs font-black text-slate-900">{line.shortLabel}</div>
                {maturityPeriods.map((period) => {
                  const value = dashboardLineValue(dashboard, line.key, period.key)
                  const active = policyLine === line.key && periodKey === period.key
                  return (
                    <button
                      key={`${line.key}-${period.key}`}
                      type="button"
                      className={`min-h-[42px] border-l border-slate-100 px-2 py-1.5 text-right transition ${active
                        ? 'bg-white text-brand-navy ring-1 ring-inset ring-brand-sky/30'
                        : 'bg-transparent text-slate-900 hover:bg-white/70'
                        }`}
                      onClick={() => changeDashboardSelection(line.key, period.key)}
                    >
                      <div className="text-lg font-black tabular-nums">{Number(value || 0).toLocaleString()}</div>
                    </button>
                  )
                })}
              </div>
            )) : null}
          </div>
        </div>
      </section>
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-slate-800">만기 계약 목록· 총 {pageInfo.totalElements.toLocaleString()}건</div>
              <button
                type="button"
                className="rounded-lg bg-brand-navy px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
                disabled={saving || isSavingAnyRow}
                onClick={addNewRow}
              >
                + 추가
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={csvBusy || saving || isSavingAnyRow}
                onClick={downloadCsv}
              >
                다운로드
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={csvBusy || saving || isSavingAnyRow}
                onClick={() => csvInputRef.current?.click()}
              >
                업로드
              </button>
              <input
                ref={csvInputRef}
                type="file"
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  handleUploadFile(file)
                }}
              />
            </div>
            <div className="mt-0.5 text-xs font-medium text-slate-500">
              {periodDescription} · {sortLabel} {sortDirectionLabel} · 총 {pageInfo.totalElements.toLocaleString()}건
              {searchQuery ? ` · 검색 ${searchQuery}` : ''}
              {hasSheetChanges ? ` · 변경 ${changedCount}건 · 추가 ${newCount}건` : ''}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={!hasSheetChanges || saving || isSavingAnyRow || csvBusy}
              onClick={discardDrafts}
            >
              취소
            </button>
            <button
              type="button"
              className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50"
              disabled={!hasSheetChanges || saving || isSavingAnyRow || csvBusy}
              onClick={saveSheet}
            >
              {saving ? '저장 중...' : '전체 변경사항 저장 (' + (changedCount + newCount) + ')'}
            </button>
            <select
              value={periodKey}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800"
              onChange={(e) => changePeriod(e.target.value)}
            >
              {maturityPeriods.map((period) => (
                <option key={period.key} value={period.key}>{period.caption}</option>
              ))}
            </select>
            <select
              value={policyLine}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800"
              onChange={(e) => changePolicyLine(e.target.value)}
            >
              <option value="ALL">전체</option>
              {policyLineOptions.map((line) => (
                <option key={line.key} value={line.key}>{line.shortLabel}</option>
              ))}
            </select>
            <form className="flex min-w-[260px] max-w-full flex-1 items-center gap-2" onSubmit={applySearch}>
              <input
                value={searchInput}
                className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800"
                placeholder="고객명/연락처/보험사/상품 검색"
                aria-label="만기 계약 검색"
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                type="submit"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                disabled={loading}
              >
                검색
              </button>
              {searchQuery ? (
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={loading}
                  onClick={clearSearch}
                >
                  초기화
                </button>
              ) : null}
            </form>
          </div>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">불러오는 중...</div>
        ) : rows.length === 0 && newRows.length === 0 ? (
          <EmptyState
            title="기간 내 만기·갱신 대상이 없습니다"
            className="py-10"
          />
        ) : (
          <div className="overflow-auto">
            <table className="sgez-sticky-edge-table w-full min-w-[1480px] table-fixed border-collapse text-left text-sm">
              <colgroup>
                <col className="w-[88px]" />
                <col className="w-[180px]" />
                <col className="w-[140px]" />
                <col className="w-[96px]" />
                <col className="w-[88px]" />
                <col className="w-[120px]" />
                <col className="w-[120px]" />
                <col className="w-[120px]" />
                <col className="w-[140px]" />
                <col className="w-[210px]" />
                <col className="w-[180px]" />
                <col className="w-[78px]" />
              </colgroup>
              <thead className="border-b border-sgez-border-strong bg-sgez-surface-sunken text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-2 py-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-slate-600 hover:text-slate-900"
                      aria-label={`남은 일 ${sortKey === 'remainingDays' ? sortDirectionLabel : '정렬'}`}
                      onClick={() => toggleSort('remainingDays')}
                    >
                      남은 일 <span className="text-[10px] text-slate-400">{sortHeaderMark('remainingDays')}</span>
                    </button>
                  </th>
                  <th className="px-2 py-3">이름</th>
                  <th className="px-2 py-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-slate-600 hover:text-slate-900"
                      aria-label={`만기일 ${sortKey === 'nextMaturityDate' ? sortDirectionLabel : '정렬'}`}
                      onClick={() => toggleSort('nextMaturityDate')}
                    >
                      만기일 <span className="text-[10px] text-slate-400">{sortHeaderMark('nextMaturityDate')}</span>
                    </button>
                  </th>
                  <th className="px-2 py-3">구분</th>
                  <th className="px-2 py-3">개인/법인</th>
                  <th className="px-2 py-3">가입경로</th>
                  <th className="px-2 py-3">차량번호</th>
                  <th className="px-2 py-3">운전자한정</th>
                  <th className="px-2 py-3">가입 보험사</th>
                  <th className="px-2 py-3">연결 계약</th>
                  <th className="px-2 py-3">메모</th>
                  <th className="px-2 py-3 text-center">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {newRows.map((draft) => {
                  const error = rowErrors[`new-${draft.tempId}`]
                  const isRowSaving = Boolean(savingRowKeys['new-' + draft.tempId])
                  return (
                    <tr key={draft.tempId} className="bg-teal-50/60 align-top">
                      <td className="whitespace-nowrap px-2 py-3 text-xs text-slate-500">{formatRemainingDays(daysUntil(draft.nextMaturityDate))}</td>
                      <td className="px-2 py-3">
                        <ManagedCustomerAutocomplete
                          value={draft.customerName}
                          onValueChange={(next) => setNewField(draft.tempId, 'customerName', next)}
                          selectedCustomerId={draft.customerRefId}
                          onSelectCustomer={(customer) => applyManagedCustomerToNewRow(draft.tempId, customer)}
                          onClearCustomer={() => clearManagedCustomerFromNewRow(draft.tempId)}
                          inputClassName={`${sheetInputClass} ${error && !String(draft.customerName || '').trim() ? 'border-rose-300' : 'border-slate-300'}`}
                          placeholder="이름"
                        />
                        <input
                          value={draft.customerPhone}
                          className={`${sheetInputClass} mt-1 ${error && !normalizePhoneDigits(draft.customerPhone) ? 'border-rose-300' : 'border-slate-300'}`}
                          placeholder="연락처"
                          onChange={(e) => setNewField(draft.tempId, 'customerPhone', formatKoreanPhoneInput(e.target.value.replace(/\D/g, '')))}
                        />
                        {error ? <div className="mt-1 text-[11px] font-semibold text-rose-600">{error}</div> : null}
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="date"
                          value={draft.nextMaturityDate}
                          className={sheetInputClass}
                          onChange={(e) => setNewField(draft.tempId, 'nextMaturityDate', e.target.value)}
                        />
                      </td>

                      <td className="px-2 py-3">
                        <select
                          value={draft.policyLine}
                          className={sheetSelectClass}
                          onChange={(e) => setNewField(draft.tempId, 'policyLine', e.target.value)}
                        >
                          {policyLineOptions.map((line) => (
                            <option key={line.key} value={line.key}>{line.shortLabel}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-3 text-xs text-slate-400">신규</td>
                      <td className="px-2 py-3">
                        <input value={draft.enrollmentChannel} className={sheetInputClass} onChange={(e) => setNewField(draft.tempId, 'enrollmentChannel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.vehicleNumber} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setNewField(draft.tempId, 'vehicleNumber', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.driverRestriction} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setNewField(draft.tempId, 'driverRestriction', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.insurerName} className={sheetInputClass} onChange={(e) => setNewField(draft.tempId, 'insurerName', e.target.value)} />
                      </td>
                      <td className="px-2 py-3 text-xs text-slate-400">-</td>
                      <td className="px-2 py-3">
                        <input value={draft.productLabel} className={sheetInputClass} onChange={(e) => setNewField(draft.tempId, 'productLabel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3 text-center">
                        <div className="flex justify-center gap-1.5">
                          <button
                            type="button"
                            className="rounded bg-teal-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
                            disabled={saving || isSavingAnyRow || !hasNewRowContent(draft)}
                            onClick={() => saveNewRow(draft)}
                          >
                            {isRowSaving ? '저장 중...' : '저장'}
                          </button>
                          <button
                            type="button"
                            className="rounded border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:opacity-50"
                            disabled={saving || isSavingAnyRow}
                            onClick={() => removeNewRow(draft.tempId)}
                          >
                            제거
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {rows.map((row) => {
                  const draft = rowDraft(row)
                  const changed = Boolean(draftRows[row.id])
                  const draftDday = daysUntil(draft.nextMaturityDate)
                  const briefingBadge = dailyBriefingAlimtalkBadge(draftDday)
                  const error = rowErrors[`row-${row.id}`]
                  const isRowSaving = Boolean(savingRowKeys['row-' + row.id])
                  return (
                    <tr
                      key={row.id}
                      className={`${policyLineTableClass(row.policyLine)} ${changed ? 'ring-1 ring-inset ring-amber-300' : ''}`}
                    >
                      <td className="px-2 py-3">
                        <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold ${draftDday == null
                          ? 'bg-slate-100 text-slate-600'
                          : draftDday <= 7
                            ? 'bg-rose-50 text-rose-700'
                            : 'bg-amber-50 text-amber-800'
                          }`}>
                          {formatRemainingDays(draftDday)}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <Link
                          className="font-semibold text-brand-navy hover:underline"
                          to={`/support/customers/${row.customerRefId}#maturity`}
                          onClick={(event) => {
                            if (!confirmDiscardChanges()) {
                              event.preventDefault()
                              return
                            }
                            if (hasSheetChanges) discardDrafts()
                          }}
                        >
                          {row.customerName || '(이름 없음)'}
                        </Link>
                        {briefingBadge || isAutoCreatedFromMaturityUpload(row) ? (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {briefingBadge ? (
                              <span
                                className="inline-flex whitespace-nowrap rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-bold text-sky-700 ring-1 ring-inset ring-sky-200"
                                title="만기 D-30, D-7 또는 D-1 계약은 당일 08:00 업무 브리핑 알림톡 발송 대상에 포함됩니다."
                              >
                                {briefingBadge}
                              </span>
                            ) : null}
                            {isAutoCreatedFromMaturityUpload(row) ? (
                              <span className="inline-flex whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700 ring-1 ring-inset ring-amber-200">
                                엑셀 자동등록
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="mt-0.5 text-xs text-slate-500">{formatPhone(row.customerPhone)}</div>
                        {error ? <div className="mt-1 text-[11px] font-semibold text-rose-600">{error}</div> : null}
                        {changed ? <div className="mt-1 text-[11px] font-bold text-amber-700">수정됨</div> : null}
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="date"
                          value={draft.nextMaturityDate}
                          className={sheetInputClass}
                          onChange={(e) => setDraftField(row, 'nextMaturityDate', e.target.value)}
                        />
                      </td>

                      <td className="px-2 py-3">
                        <select
                          value={draft.policyLine}
                          className={sheetSelectClass}
                          onChange={(e) => setDraftField(row, 'policyLine', e.target.value)}
                        >
                          {policyLineOptions.map((line) => (
                            <option key={line.key} value={line.key}>{line.shortLabel}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-3">{row.customerSegment || '-'}</td>
                      <td className="px-2 py-3">
                        <input value={draft.enrollmentChannel} className={sheetInputClass} onChange={(e) => setDraftField(row, 'enrollmentChannel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.vehicleNumber} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setDraftField(row, 'vehicleNumber', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.driverRestriction} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setDraftField(row, 'driverRestriction', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.insurerName} className={sheetInputClass} onChange={(e) => setDraftField(row, 'insurerName', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <div className="truncate text-xs text-slate-600" title={contractLabel(row.linkedContract)}>
                          {row.linkedContract ? contractLabel(row.linkedContract) : '-'}
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.productLabel} className={sheetInputClass} onChange={(e) => setDraftField(row, 'productLabel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex justify-center gap-1.5">
                          {changed ? (
                            <button
                              type="button"
                              className="rounded bg-teal-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
                              disabled={saving || isSavingAnyRow}
                              onClick={() => saveExistingRow(row)}
                            >
                              {isRowSaving ? '저장 중...' : '저장'}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="rounded border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            disabled={saving || isSavingAnyRow}
                            onClick={() => closeRow(row)}
                          >
                            종료
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-col gap-2 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500">
            {pageInfo.totalPages > 0 ? `${pageInfo.page + 1} / ${pageInfo.totalPages} 페이지` : '0 / 0 페이지'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={!canPrev || loading}
              onClick={() => goToPage(pageInfo.page - 1)}
            >
              이전
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={!canNext || loading}
              onClick={() => goToPage(pageInfo.page + 1)}
            >
              다음
            </button>
          </div>
        </div>
      </section>
    </PageFrame>
  )
}
